// cSpell:ignore kosten Totaal
// A settlement card's VAT per rate (Lewis, 2026-10-19): the "i" of its
// "BTW" line, one line per rate that occurs on the card, "21%" · € 985,60
// · € 206,98. Built from the card's own rows (each its invoice's rate, its
// share excl. VAT and its exact VAT), so the lines add up to the card's
// "Totaal kosten excl. btw" and "BTW" to the cent. A rate without any
// amount is not listed; a row whose rate is not known (its costs carry
// different rates) is listed as another rate, never guessed.

import { fromCents, toCents } from "./money.utils";
import { VAT_OPTIONS, VatValue } from "./types";

export interface IVatRateRow {
  excl: number;
  vat: number;
  // Undefined when not known.
  rate?: VatValue;
}

export interface IVatRateLine {
  rate?: VatValue;
  excl: number;
  vat: number;
}

const keyOf = (rate?: VatValue): string =>
  rate == null ? "unknown" : String(rate);

// The highest percentage first, then the reasons without VAT in the order
// the cost form offers them, then the rows of an unknown rate.
const orderOf = (rate?: VatValue): number => {
  if (rate == null) return Number.MAX_SAFE_INTEGER;
  if (typeof rate === "number") return -rate;
  return 1000 + VAT_OPTIONS.indexOf(rate);
};

export const vatByRate = (
  rows: IVatRateRow[],
): { lines: IVatRateLine[]; total: { excl: number; vat: number } } => {
  const byRate = new Map<
    string,
    { rate?: VatValue; exclCents: number; vatCents: number }
  >();
  for (const row of rows) {
    const exclCents = toCents(row.excl);
    const vatCents = toCents(row.vat);
    if (exclCents === 0 && vatCents === 0) continue;
    const key = keyOf(row.rate);
    const line = byRate.get(key) ?? {
      rate: row.rate,
      exclCents: 0,
      vatCents: 0,
    };
    line.exclCents += exclCents;
    line.vatCents += vatCents;
    byRate.set(key, line);
  }
  const lines = [...byRate.values()]
    .sort((a, b) => orderOf(a.rate) - orderOf(b.rate))
    .map(line => ({
      ...(line.rate != null ? { rate: line.rate } : {}),
      excl: fromCents(line.exclCents),
      vat: fromCents(line.vatCents),
    }));
  return {
    lines,
    total: {
      excl: fromCents(lines.reduce((sum, line) => sum + toCents(line.excl), 0)),
      vat: fromCents(lines.reduce((sum, line) => sum + toCents(line.vat), 0)),
    },
  };
};

// The one rate of a row's costs, or undefined when they carry several.
export const commonVatRate = (
  costs: { vatPct: VatValue }[],
): VatValue | undefined => {
  const rates = new Set(costs.map(cost => keyOf(cost.vatPct)));
  return rates.size === 1 ? costs[0].vatPct : undefined;
};
